import React, { useEffect, useState } from 'react';
import ReusableDataGrid from '../ReusableDataGrid.tsx';
import { GridColDef, GridRowModel, GridRowsProp } from '@mui/x-data-grid';
import { getTariffs, createTariff, updateTariff, deleteTariff } from '../../services/api.ts';
import { GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid';
import TextField from '@mui/material/TextField';

function TimeEditCell(params: GridRenderEditCellParams) {
  const { id, field, value } = params;
  const apiRef = useGridApiContext();

  const toTimeString = (v: number) => {
    const h = Math.floor(v).toString().padStart(2, '0');
    const m = Math.round((v % 1) * 60).toString().padStart(2, '0');
    return `${h}:${m}`;
  };

  const fromTimeString = (s: string) => {
    const [h, m] = s.split(':').map(Number);
    return h + m / 60;
  };

  return (
    <TextField
      type="time"
      value={toTimeString(value ?? 0)}
      onChange={e => apiRef.current.setEditCellValue({ id, field, value: fromTimeString(e.target.value) })}
      size="small"
      InputLabelProps={{ shrink: true }}
      sx={{
        width: '100%',
        '& .MuiOutlinedInput-root': {
          fontFamily: "'DM Mono', monospace",
          fontSize: 13,
          '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
          '&:hover fieldset': { borderColor: '#1D9E75' },
          '&.Mui-focused fieldset': { borderColor: '#1D9E75' },
        },
      }}
    />
  );
}

const TAG_MAP: Record<string, string> = {
  elec:  'Electricity',
  gas:   'Gas',
  water: 'Water',
};

const TAG_REVERSE_MAP: Record<string, string> = {
  Electricity: 'elec',
  Gas:         'gas',
  Water:       'water',
};

const defaultRow = {
  type:      'Electricity',
  rate:      0,
  startHour: 0,
  endHour:   24,
  dayType:   0,
  validFrom: new Date('2020-01-01'),
  validTo:   null as Date | null,
};

const columns: GridColDef[] = [
  {
    field:        'type',
    headerName:   'Type',
    type:         'singleSelect',
    minWidth:     80,
    flex:         1,
    valueOptions: ['Electricity', 'Gas', 'Water'],
    editable:     true,
  },
  {
    field:       'rate',
    headerName:  'Rate',
    type:        'number',
    minWidth:    80,
    flex:        0.75,
    align:       'left',
    headerAlign: 'left',
    editable:    true,
    valueFormatter: value =>
      value != null ? `£${Number(value).toLocaleString('en-GB', { minimumFractionDigits: 2 })}` : '',
  },
  {
    field:      'startHour',
    headerName: 'Start Hour',
    type:       'number',
    minWidth:   80,
    flex:       0.75,
    editable:   true,
    valueFormatter: value => {
      if (value == null) return '';
      const h = Math.floor(value).toString().padStart(2, '0');
      const m = Math.round((value % 1) * 60).toString().padStart(2, '0');
      return `${h}:${m}`;
    },
    renderEditCell: params => <TimeEditCell {...params} />,
  },
  {
    field:      'endHour',
    headerName: 'End Hour',
    type:       'number',
    minWidth:   80,
    flex:       0.75,
    editable:   true,
    valueFormatter: value => {
      if (value == null) return '';
      const h = Math.floor(value).toString().padStart(2, '0');
      const m = Math.round((value % 1) * 60).toString().padStart(2, '0');
      return `${h}:${m}`;
    },
    renderEditCell: params => <TimeEditCell {...params} />,
  },
  {
    field:        'dayType',
    headerName:   'Weekday',
    type:         'singleSelect',
    minWidth:     80,
    flex:         1,
    editable:     true,
    valueOptions: [
      { value: 7, label: 'All'       },
      { value: 0, label: 'Monday'    },
      { value: 1, label: 'Tuesday'   },
      { value: 2, label: 'Wednesday' },
      { value: 3, label: 'Thursday'  },
      { value: 4, label: 'Friday'    },
      { value: 5, label: 'Saturday'  },
      { value: 6, label: 'Sunday'    },
    ],
  },
  {
    field:          'validFrom',
    headerName:     'Valid From',
    type:           'date',
    minWidth:       100,
    flex:           1,
    editable:       true,
    valueFormatter: params => params?.toLocaleDateString('en-GB'),
  },
  {
    field:          'validTo',
    headerName:     'Valid To',
    type:           'date',
    minWidth:       100,
    flex:           1,
    editable:       true,
    valueFormatter: params => params?.toLocaleDateString('en-GB') ?? '',
  },
];

const mapRow = (row: any) => ({
  id:        row.id,
  type:      TAG_MAP[row.tag] ?? row.tag,
  rate:      row.rate,
  startHour: row.start_hour,
  endHour:   row.end_hour,
  dayType:   row.weekday,
  validFrom: row.valid_from ? new Date(row.valid_from) : null,
  validTo:   row.valid_to   ? new Date(row.valid_to)   : null,
});

export default function TariffTable() {
  const [initialRows, setInitialRows] = useState<GridRowsProp>([]);

  useEffect(() => {
    getTariffs()
      .then(data => setInitialRows(data.map(mapRow)))
      .catch(err  => console.error('Failed to fetch tariffs:', err));
  }, []);

  const handleSaveRow = async (row: GridRowModel): Promise<GridRowModel> => {
    const basePayload = {
      rate:       row.rate,
      valid_from: row.validFrom instanceof Date ? row.validFrom.toISOString() : row.validFrom,
      valid_to:   row.validTo  instanceof Date ? row.validTo.toISOString()   : row.validTo ?? null,
      tag:        TAG_REVERSE_MAP[row.type] ?? row.type,
      start_hour: row.startHour,
      end_hour:   row.endHour,
    };

    if (row.dayType === 7 && !row.isNew) {
      await deleteTariff(row.id);
      await Promise.all([0,1,2,3,4,5,6].map(weekday => createTariff({ ...basePayload, weekday })));
      getTariffs().then(data => setInitialRows(data.map(mapRow)));
      return { ...row, isNew: false };
    }

    if (row.isNew) {
      const weekdays = row.dayType === 7 ? [0,1,2,3,4,5,6] : [row.dayType];
      const results  = await Promise.all(weekdays.map(weekday => createTariff({ ...basePayload, weekday })));
      return { ...row, id: results[0][0].id, isNew: false };
    }

    await updateTariff(row.id, { ...basePayload, weekday: row.dayType });
    return { ...row, isNew: false };
  };

  const handleDeleteRow = async (row: GridRowModel): Promise<void> => {
    await deleteTariff(row.id);
  };

  return (
    <ReusableDataGrid
      defaultRow={defaultRow}
      initialRows={initialRows}
      columns={columns}
      height="75vh"
      width="100%"
      onSaveRow={handleSaveRow}
      onDeleteRow={handleDeleteRow}
    />
  );
}
